import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { AppInput, AppSelect } from "../../components/appComponents";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "../../components/ui/avatar";
import { Icon } from "@iconify/react";
import Common from "../../helper/common";
import {
  respEnum,
  AmericanoAccessTypeEnum,
  AmericanoTypeEnum,
} from "../../constants/appConstant";
import toast from "react-hot-toast";
import { Alert, AlertDescription } from "../../components/ui/alert";

const CreateAmericano = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formulaLoading, setFormulaLoading] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [type, setType] = useState(String(AmericanoTypeEnum.Americano)); // Default: Americano
  const [points, setPoints] = useState(32);
  const [courts, setCourts] = useState([{ court_name: "Court 1" }]);
  const [players, setPlayers] = useState([]);

  // Player lookup modal state
  const [addPlayerModalOpen, setAddPlayerModalOpen] = useState(false);
  const [modalPhoneCode, setModalPhoneCode] = useState("+92");
  const [modalPhone, setModalPhone] = useState("");
  const [modalPlayerData, setModalPlayerData] = useState(null);
  const [modalLookupLoading, setModalLookupLoading] = useState(false);
  const [modalAccessType, setModalAccessType] = useState(
    String(AmericanoAccessTypeEnum.NoAccess)
  ); // Default: NoAccess = 1

  // Formula results
  const [formulaResults, setFormulaResults] = useState(null);

  // Schedule preview state
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleData, setScheduleData] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const phoneCodes = [
    { value: "+92", label: "+92 (PK)" },
    { value: "+1", label: "+1 (US/CA)" },
    { value: "+44", label: "+44 (UK)" },
    { value: "+971", label: "+971 (UAE)" },
    { value: "+966", label: "+966 (SA)" },
  ];

  // Note: phoneInputRef removed as AppInput doesn't support ref forwarding
  // const phoneInputRef = React.useRef(null);

  // Development: Load default players on mount
  useEffect(() => {
    const loadDefaultPlayers = async () => {
      const defaultPhones = [
        "3089195907",
        "3158250579",
        "3428238086",
        "3343025343",
      ];
      const defaultPhoneCode = "+92";

      const loadedPlayers = [];

      for (const phone of defaultPhones) {
        try {
          const user = await lookupUserByPhone(defaultPhoneCode, phone);
          if (user) {
            // First player gets FullAccess (2), others get NoAccess (1)
            const accessType =
              loadedPlayers.length === 0
                ? AmericanoAccessTypeEnum.FullAccess
                : AmericanoAccessTypeEnum.NoAccess;

            loadedPlayers.push({
              player_id: user.id,
              phone_code: user.phoneCode || defaultPhoneCode,
              phone: user.phone || phone,
              access_type: accessType,
              firstName: user.firstName || "",
              lastName: user.lastName || "",
              image: user.image || null,
              level: user.level || null,
            });
          }
        } catch (error) {
          console.error(`Error loading default player ${phone}:`, error);
        }
      }

      if (loadedPlayers.length > 0) {
        setPlayers(loadedPlayers);
      }
    };

    // Only load in development
    if (
      process.env.NODE_ENV === "development" ||
      window.location.hostname === "localhost"
    ) {
      loadDefaultPlayers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus phone input when modal opens - Note: AppInput doesn't support ref forwarding
  // The focus will be handled by the modal's auto-focus if needed
  // useEffect(() => {
  //   if (addPlayerModalOpen && phoneInputRef.current) {
  //     const timer = setTimeout(() => {
  //       phoneInputRef.current?.focus();
  //     }, 150);
  //     return () => clearTimeout(timer);
  //   }
  // }, [addPlayerModalOpen]);

  // Lookup user by phone
  const lookupUserByPhone = useCallback(async (phoneCode, phone) => {
    if (!phone || phone.length < 10) {
      return null;
    }

    try {
      const response = await Common.ApiService.getInstance().request(
        `CheckUserExistWithLevel?phone=${encodeURIComponent(phone)}&sportId=1`,
        null,
        "GET",
        false
      );

      if (response?.status === respEnum.Success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Error looking up player:", error);
      return null;
    }
  }, []);

  // Calculate formula when players, courts, or points change
  useEffect(() => {
    if (players.length >= 4 && courts.length >= 1 && points > 0) {
      calculateFormula();
    } else {
      setFormulaResults(null);
    }
  }, [players.length, courts.length, points]);

  // Load schedule when showSchedule is true
  useEffect(() => {
    if (
      showSchedule &&
      players.length >= 4 &&
      courts.length >= 1 &&
      points > 0 &&
      formulaResults
    ) {
      loadSchedulePreview();
    } else if (!showSchedule) {
      setScheduleData([]);
    }
  }, [showSchedule, players.length, courts.length, points, formulaResults]);

  const calculateFormula = async () => {
    if (players.length < 4 || courts.length < 1 || points <= 0) {
      setFormulaResults(null);
      return;
    }

    setFormulaLoading(true);
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetAmericanoFormula",
        {
          no_of_players: players.length,
          no_of_courts: courts.length,
          points: points,
        },
        "Post"
      );

      if (response?.status === respEnum.Success && response.data?.[0]) {
        setFormulaResults(response.data[0]);
      } else {
        setFormulaResults(null);
      }
    } catch (error) {
      console.error("Error calculating formula:", error);
      setFormulaResults(null);
    } finally {
      setFormulaLoading(false);
    }
  };

  // Load schedule preview
  const loadSchedulePreview = async () => {
    if (
      players.length < 4 ||
      courts.length < 1 ||
      points <= 0 ||
      !formulaResults
    ) {
      return;
    }

    setScheduleLoading(true);
    try {
      const playerIds = players.map((p) => p.player_id);
      const response = await Common.ApiService.getInstance().request(
        "GetAmericanoScheduleHelper",
        {
          player_ids: playerIds,
          no_of_courts: courts.length,
          total_matches: formulaResults.total_matches,
          points: points,
        },
        "Post"
      );

      if (response?.status === respEnum.Success && response.data) {
        // Map player IDs to player names
        const scheduleWithNames = response.data.map((match) => ({
          ...match,
          players: match.players.map((p) => {
            const player = players.find((pl) => pl.player_id === p.player_id);
            return {
              ...p,
              player_name: player
                ? `${player.firstName} ${player.lastName}`.trim()
                : `Player ${p.player_id}`,
              player_image: player?.image || null,
            };
          }),
        }));
        setScheduleData(scheduleWithNames);
      } else {
        setScheduleData([]);
        toast.error(response.message || "Failed to load schedule");
      }
    } catch (error) {
      console.error("Error loading schedule:", error);
      setScheduleData([]);
      toast.error("Error loading schedule preview");
    } finally {
      setScheduleLoading(false);
    }
  };

  // Handle player lookup
  const handlePlayerLookup = useCallback(async () => {
    if (!modalPhone || modalPhone.length < 10) {
      setModalPlayerData(null);
      toast.error("Please enter a valid phone number (at least 10 digits)");
      return;
    }

    // Check if player already added
    const existingPlayer = players.find(
      (p) => p.phone === modalPhone && p.phone_code === modalPhoneCode
    );
    if (existingPlayer) {
      toast.error("This player is already added");
      return;
    }

    setModalLookupLoading(true);
    try {
      const user = await lookupUserByPhone(modalPhoneCode, modalPhone);
      if (user) {
        setModalPlayerData({
          id: user.id,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          image: user.image || null,
          level: user.level || null,
          phoneCode: user.phoneCode || modalPhoneCode,
          phone: user.phone || modalPhone,
        });
        toast.success("Player found!");
      } else {
        setModalPlayerData(null);
        toast.error("Player not found in PlayPro");
      }
    } catch (error) {
      console.error("Error looking up player:", error);
      toast.error("Error looking up player");
      setModalPlayerData(null);
    } finally {
      setModalLookupLoading(false);
    }
  }, [modalPhone, modalPhoneCode, lookupUserByPhone, players]);

  // Add player to list
  const handleAddPlayer = () => {
    if (!modalPlayerData) {
      toast.error("Please search and verify player first");
      return;
    }

    // First player gets FullAccess (2), others get selected access type
    const accessType =
      players.length === 0
        ? AmericanoAccessTypeEnum.FullAccess
        : parseInt(modalAccessType);

    const newPlayer = {
      player_id: modalPlayerData.id,
      phone_code: modalPhoneCode,
      phone: modalPhone,
      access_type: accessType,
      firstName: modalPlayerData.firstName,
      lastName: modalPlayerData.lastName,
      image: modalPlayerData.image,
      level: modalPlayerData.level,
    };

    setPlayers([...players, newPlayer]);
    setAddPlayerModalOpen(false);
    setModalPlayerData(null);
    setModalPhone("");
    setModalPhoneCode("+92");
    setModalAccessType(String(AmericanoAccessTypeEnum.NoAccess)); // Reset to NoAccess for next player
    toast.success("Player added successfully!");
  };

  // Remove player from list
  const handleRemovePlayer = (index) => {
    const newPlayers = players.filter((_, i) => i !== index);
    setPlayers(newPlayers);
    toast.success("Player removed");
  };

  // Calculate max courts based on players: floor(N / 4)
  const maxCourts = useMemo(() => {
    if (players.length < 4) return 1;
    return Math.floor(players.length / 4);
  }, [players.length]);

  // Calculate recommended courts
  const recommendedCourts = useMemo(() => {
    if (players.length < 4) return 1;
    return Math.floor(players.length / 4);
  }, [players.length]);

  // Add court
  const handleAddCourt = () => {
    if (courts.length >= maxCourts) {
      toast.error(
        `Maximum ${maxCourts} court(s) allowed for ${players.length} players. Each match requires 4 players.`
      );
      return;
    }
    setCourts([...courts, { court_name: `Court ${courts.length + 1}` }]);
  };

  // Remove court
  const handleRemoveCourt = (index) => {
    if (courts.length > 1) {
      const newCourts = courts.filter((_, i) => i !== index);
      setCourts(newCourts);
    } else {
      toast.error("At least one court is required");
    }
  };

  // Auto-adjust courts when players change
  useEffect(() => {
    if (players.length >= 4 && courts.length > maxCourts) {
      // Remove excess courts
      const newCourts = courts.slice(0, maxCourts);
      setCourts(newCourts);
      toast.error(
        `Courts reduced to ${maxCourts} (maximum allowed for ${players.length} players)`,
        { duration: 4000 }
      );
    }
  }, [players.length, maxCourts, courts.length]);

  // Update court name
  const handleUpdateCourtName = (index, courtName) => {
    const newCourts = [...courts];
    newCourts[index].court_name = courtName;
    setCourts(newCourts);
  };

  // Validate form
  const validateForm = () => {
    if (!name || name.trim() === "") {
      toast.error("Tournament name is required");
      return false;
    }

    if (points <= 0) {
      toast.error("Points must be greater than 0");
      return false;
    }

    if (courts.length < 1) {
      toast.error("At least one court is required");
      return false;
    }

    if (players.length < 4) {
      toast.error("At least 4 players are required");
      return false;
    }

    // Check if at least one player has FullAccess
    const hasFullAccess = players.some(
      (p) => p.access_type === AmericanoAccessTypeEnum.FullAccess
    );
    if (!hasFullAccess) {
      toast.error("At least one player must have Full Access");
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const currentUser = Common.getUser();
      const creatorPlayer = players.find(
        (p) => p.player_id === currentUser?.id
      );

      const formData = {
        name: name.trim(),
        type: parseInt(type),
        points: points,
        player_id:
          currentUser?.id || creatorPlayer?.player_id || players[0].player_id,
        courts: courts.map((c) => ({ court_name: c.court_name })),
        players: players.map((p) => ({
          phone_code: p.phone_code,
          phone: p.phone,
          access_type: p.access_type,
        })),
      };

      const response = await Common.ApiService.getInstance().request(
        "CreateAmericano",
        formData,
        "Post"
      );

      if (response?.status === respEnum.Success) {
        toast.success(response.message || "Americano created successfully");
        navigate("/app/americanos");
      } else {
        toast.error(response.message || "Error creating americano");
      }
    } catch (error) {
      toast.error(error.message || "Error creating americano");
    } finally {
      setLoading(false);
    }
  };

  // Calculate theoretical matches (from calculator)
  const theoreticalMatches = useMemo(() => {
    if (players.length < 4) return null;
    return (players.length * (players.length - 1)) / 4;
  }, [players.length]);

  const isPerfectPairing = useMemo(() => {
    if (!theoreticalMatches) return false;
    return Number.isInteger(theoreticalMatches);
  }, [theoreticalMatches]);

  return (
    <div className="container mx-auto p-3 max-w-5xl">
      <Card className="border-2 border-primary/20 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5 pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-bold text-primary">
              Create Americano
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/app/americanos")}
              className="h-8"
            >
              <Icon
                icon="heroicons:arrow-left"
                className="h-3.5 w-3.5 mr-1.5"
              />
              Back
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-3">
          <div className="space-y-3">
            {/* Basic Information Section */}
            <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-200/50">
              <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Icon icon="heroicons:information-circle" className="h-4 w-4" />
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <AppInput
                    label="Tournament Name *"
                    value={name}
                    onChange={setName}
                    placeholder="Enter tournament name"
                    className="h-8 text-sm"
                    size="sm"
                  />
                </div>
                <div>
                  <AppInput
                    label="Points per Match *"
                    type="number"
                    min="1"
                    size="sm"
                    value={points}
                    onChange={(value) => setPoints(parseInt(value) || 32)}
                    placeholder="32"
                    className="h-8 text-sm"
                  />
                  {points > 0 && points % 4 !== 0 && (
                    <p className="text-xs text-amber-600 mt-0.5">
                      ⚠ Recommended: Divisible by 4
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Courts Section */}
            <div className="bg-green-50/50 rounded-lg p-3 border border-green-200/50">
              <div className="mb-2">
                <h3 className="text-sm font-semibold text-green-900 flex items-center gap-2 mb-1">
                  <Icon icon="heroicons:map-pin" className="h-4 w-4" />
                  Courts * ({courts.length}/{maxCourts} max)
                </h3>
                {players.length >= 4 && (
                  <p className="text-xs text-green-700">
                    Recommended: {recommendedCourts} court(s) for{" "}
                    {players.length} players
                  </p>
                )}
              </div>
              {players.length >= 4 && courts.length >= maxCourts && (
                <Alert className="mb-2 py-1.5 bg-amber-50 border-amber-200">
                  <AlertDescription className="text-xs text-amber-800">
                    ⚠ Maximum {maxCourts} court(s) allowed. Each match requires
                    4 players.
                  </AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                {courts.map((court, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-1">
                      <AppInput
                        size="sm"
                        value={court.court_name}
                        onChange={(value) =>
                          handleUpdateCourtName(index, value)
                        }
                        placeholder={`Court ${index + 1}`}
                        className="h-8 text-sm"
                        removeWrapper={true}
                      />
                    </div>
                    {index === courts.length - 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddCourt}
                        disabled={courts.length >= maxCourts}
                        className="h-8 text-xs bg-green-100 hover:bg-green-200 border-green-300 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                      >
                        <Icon icon="heroicons:plus" className="h-3 w-3 mr-1" />
                        Add
                      </Button>
                    )}
                    {courts.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => handleRemoveCourt(index)}
                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                      >
                        <Icon icon="heroicons:trash" className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Players Section */}
            <div className="bg-purple-50/50 rounded-lg p-3 border border-purple-200/50">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-purple-900 flex items-center gap-2">
                  <Icon icon="heroicons:users" className="h-4 w-4" />
                  Players * ({players.length})
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAddPlayerModalOpen(true)}
                  className="h-7 text-xs bg-purple-100 hover:bg-purple-200 border-purple-300"
                >
                  <Icon icon="heroicons:plus" className="h-3 w-3 mr-1" />
                  Add Player
                </Button>
              </div>

              {players.length < 4 && (
                <Alert variant="destructive" className="mb-2 py-1.5">
                  <AlertDescription className="text-xs">
                    ⚠ At least 4 players required
                  </AlertDescription>
                </Alert>
              )}

              {players.length > 0 && players.length % 4 !== 0 && (
                <Alert className="mb-2 py-1.5 bg-amber-50 border-amber-200">
                  <AlertDescription className="text-xs text-amber-800">
                    ⚠ {players.length} players not divisible by 4. Recommended:
                    4, 8, 12, 16, 20
                  </AlertDescription>
                </Alert>
              )}

              {!isPerfectPairing && theoreticalMatches && (
                <Alert className="mb-2 py-1.5 bg-amber-50 border-amber-200">
                  <AlertDescription className="text-xs text-amber-800">
                    ⚠ Perfect pairing not possible with {players.length} players
                  </AlertDescription>
                </Alert>
              )}

              {players.length === 0 ? (
                <div className="text-center py-6 text-sm text-gray-500">
                  <Icon
                    icon="heroicons:user-plus"
                    className="h-8 w-8 mx-auto mb-2 opacity-50"
                  />
                  <p>No players added yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {players.map((player, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 border rounded-md bg-white hover:shadow-sm transition-shadow"
                    >
                      <Avatar className="h-8 w-8">
                        {player.image ? (
                          <AvatarImage
                            src={player.image}
                            alt={player.firstName}
                          />
                        ) : (
                          <AvatarFallback className="text-xs bg-purple-100 text-purple-700">
                            {player.firstName?.[0] || "P"}
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">
                          {player.firstName} {player.lastName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {player.phone_code} {player.phone}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <AppSelect
                          value={String(player.access_type)}
                          onChange={(value) => {
                            const newPlayers = [...players];
                            newPlayers[index].access_type = parseInt(value);
                            setPlayers(newPlayers);
                          }}
                          data={[
                            {
                              id: String(AmericanoAccessTypeEnum.NoAccess),
                              name: "No Access",
                            },
                            {
                              id: String(AmericanoAccessTypeEnum.FullAccess),
                              name: "Full Access",
                            },
                          ]}
                          className="h-6 w-24 text-xs"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemovePlayer(index)}
                          className="h-6 w-6 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Icon
                            icon="heroicons:trash"
                            className="h-3.5 w-3.5"
                          />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Formula Results Section */}
            {formulaResults && (
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-3 border-2 border-indigo-200">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-indigo-900 flex items-center gap-2">
                    <Icon icon="heroicons:chart-bar" className="h-4 w-4" />
                    Tournament Statistics
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSchedule(!showSchedule)}
                    className="h-7 text-xs bg-indigo-100 hover:bg-indigo-200 border-indigo-300"
                  >
                    <Icon
                      icon={
                        showSchedule ? "heroicons:eye-slash" : "heroicons:eye"
                      }
                      className="h-3 w-3 mr-1"
                    />
                    {showSchedule ? "Hide" : "Show"} Schedule
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div className="bg-white rounded-md p-2 border border-indigo-100 shadow-sm">
                    <p className="text-xs text-gray-600 mb-0.5">
                      Total Matches
                    </p>
                    <p className="text-lg font-bold text-indigo-700">
                      {formulaResults.total_matches || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded-md p-2 border border-indigo-100 shadow-sm">
                    <p className="text-xs text-gray-600 mb-0.5">
                      Estimated Duration
                    </p>
                    <p className="text-lg font-bold text-indigo-700">
                      {formulaResults.estimated_duration_hours?.toFixed(2) || 0}{" "}
                      hrs
                    </p>
                  </div>
                  <div className="bg-white rounded-md p-2 border border-indigo-100 shadow-sm">
                    <p className="text-xs text-gray-600 mb-0.5">
                      Matches per Player
                    </p>
                    <p className="text-lg font-bold text-indigo-700">
                      {formulaResults.matches_per_players || 0}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Schedule Preview Section */}
            {showSchedule && formulaResults && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-3 border-2 border-amber-200">
                <h3 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                  <Icon icon="heroicons:calendar-days" className="h-4 w-4" />
                  Schedule Preview
                </h3>
                {scheduleLoading ? (
                  <div className="text-center py-4">
                    <Icon
                      icon="svg-spinners:3-dots-fade"
                      className="h-5 w-5 mx-auto text-amber-600"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Loading schedule...
                    </p>
                  </div>
                ) : scheduleData.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {scheduleData.map((match, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-md p-2 border border-amber-100 shadow-sm"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                              Round {match.round}
                            </span>
                            <span className="text-xs text-gray-600">
                              {match.court_name}
                            </span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-1.5">
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-700">
                              Team A
                            </p>
                            {match.players
                              .filter((p) => p.side === "A")
                              .sort(
                                (a, b) => (a.position || 0) - (b.position || 0)
                              )
                              .map((player, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center gap-2 text-xs"
                                >
                                  <Avatar className="h-6 w-6">
                                    {player.player_image ? (
                                      <AvatarImage
                                        src={player.player_image}
                                        alt={player.player_name}
                                      />
                                    ) : (
                                      <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                                        {player.player_name?.[0] || "P"}
                                      </AvatarFallback>
                                    )}
                                  </Avatar>
                                  <span className="truncate">
                                    {player.player_name}
                                  </span>
                                </div>
                              ))}
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-700">
                              Team B
                            </p>
                            {match.players
                              .filter((p) => p.side === "B")
                              .sort(
                                (a, b) => (a.position || 0) - (b.position || 0)
                              )
                              .map((player, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center gap-2 text-xs"
                                >
                                  <Avatar className="h-6 w-6">
                                    {player.player_image ? (
                                      <AvatarImage
                                        src={player.player_image}
                                        alt={player.player_name}
                                      />
                                    ) : (
                                      <AvatarFallback className="text-xs bg-red-100 text-red-700">
                                        {player.player_name?.[0] || "P"}
                                      </AvatarFallback>
                                    )}
                                  </Avatar>
                                  <span className="truncate">
                                    {player.player_name}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm text-gray-500">
                    No schedule available. Please ensure you have at least 4
                    players.
                  </div>
                )}
              </div>
            )}

            {formulaLoading && (
              <div className="text-center py-2">
                <Icon
                  icon="svg-spinners:3-dots-fade"
                  className="h-5 w-5 mx-auto text-primary"
                />
                <p className="text-xs text-gray-500 mt-1">Calculating...</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/app/americanos")}
                className="h-8"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="h-8 bg-primary hover:bg-primary/90"
              >
                {loading ? (
                  <>
                    <Icon
                      icon="svg-spinners:3-dots-fade"
                      className="h-3.5 w-3.5 mr-1.5"
                    />
                    Creating...
                  </>
                ) : (
                  <>
                    <Icon
                      icon="heroicons:check"
                      className="h-3.5 w-3.5 mr-1.5"
                    />
                    Create Americano
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Player Modal */}
      <Dialog open={addPlayerModalOpen} onOpenChange={setAddPlayerModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="pb-3">
            <DialogTitle className="text-base font-semibold">
              Add Player
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-end">
                <AppSelect
                  label="Phone Code"
                  value={modalPhoneCode}
                  onChange={setModalPhoneCode}
                  data={phoneCodes.map((code) => ({
                    id: code.value,
                    name: code.label,
                  }))}
                  className="h-10 text-sm"
                />
              </div>
              <div className="col-span-2 flex items-end">
                <AppInput
                  label="Phone Number"
                  type="tel"
                  size="sm"
                  value={modalPhone}
                  onChange={setModalPhone}
                  placeholder="Enter phone number"
                  className="h-10 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handlePlayerLookup();
                    }
                  }}
                />
              </div>
            </div>

            <div>
              <AppSelect
                label="Access Type"
                value={modalAccessType}
                onChange={setModalAccessType}
                disabled={players.length === 0}
                data={[
                  {
                    id: String(AmericanoAccessTypeEnum.NoAccess),
                    name: "No Access",
                  },
                  {
                    id: String(AmericanoAccessTypeEnum.FullAccess),
                    name: "Full Access",
                  },
                ]}
                className="h-10 text-sm"
              />
              {players.length === 0 && (
                <p className="text-xs text-gray-500 mt-1.5">
                  First player will get Full Access automatically
                </p>
              )}
            </div>

            <Button
              onClick={handlePlayerLookup}
              disabled={modalLookupLoading || !modalPhone}
              className="w-full h-10 bg-purple-600 hover:bg-purple-700 text-white font-medium"
            >
              {modalLookupLoading ? (
                <>
                  <Icon
                    icon="svg-spinners:3-dots-fade"
                    className="h-4 w-4 mr-2"
                  />
                  Searching...
                </>
              ) : (
                <>
                  <Icon
                    icon="heroicons:magnifying-glass"
                    className="h-4 w-4 mr-2"
                  />
                  Search Player
                </>
              )}
            </Button>

            {modalPlayerData && (
              <div className="p-3 border rounded-lg bg-green-50 border-green-200">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10 shrink-0">
                    {modalPlayerData.image ? (
                      <AvatarImage
                        src={modalPlayerData.image}
                        alt={modalPlayerData.firstName}
                      />
                    ) : (
                      <AvatarFallback className="bg-green-100 text-green-700">
                        {modalPlayerData.firstName?.[0] || "P"}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {modalPlayerData.firstName} {modalPlayerData.lastName}
                    </p>
                    {modalPlayerData.level && (
                      <p className="text-xs text-gray-600">
                        Level: {modalPlayerData.level}
                      </p>
                    )}
                  </div>
                  <Icon
                    icon="heroicons:check-circle"
                    className="h-5 w-5 text-green-600 shrink-0"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-3 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setAddPlayerModalOpen(false);
                setModalPlayerData(null);
                setModalPhone("");
                setModalPhoneCode("+92");
                setModalAccessType(String(AmericanoAccessTypeEnum.NoAccess));
              }}
              className="h-9 px-4"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddPlayer}
              disabled={!modalPlayerData}
              className="h-9 px-4 bg-green-600 hover:bg-green-700 text-white"
            >
              Add Player
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateAmericano;
